/**
 * DashboardHeader — barre supérieure fixe du cockpit UNIQUE.
 * Affiche : logo, heure, compteurs Q1/Q2, alertes, profil + logout.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Bell, LogOut, Sparkles, Zap, ZapOff } from 'lucide-react';
import { useUserStore } from '@store/userStore';
import { useTaskStore } from '@store/taskStore';
import { formatHourMinute } from '@utils/dateUtils';
import { AIService } from '@services/AIService';

export function DashboardHeader(): JSX.Element {
  const profile = useUserStore((s) => s.profile);
  const logout = useUserStore((s) => s.logout);
  const counts = useTaskStore((s) => s.countByQuadrant)();
  const [now, setNow] = useState<string>(() => formatHourMinute());
  const [aiStatus, setAiStatus] = useState<'checking' | 'online' | 'offline'>(
    'checking',
  );
  const [aiModel, setAiModel] = useState<string>('');

  useEffect(() => {
    const id = window.setInterval(() => setNow(formatHourMinute()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const h = await AIService.health();
        if (cancelled) return;
        setAiStatus('online');
        setAiModel(h.model);
      } catch {
        if (!cancelled) setAiStatus('offline');
      }
    };
    void check();
    const id = window.setInterval(check, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '—';
  const initials = profile
    ? `${profile.firstName.charAt(0)}${profile.lastName.charAt(0) || ''}`.toUpperCase()
    : '??';

  return (
    <header
      className="sticky top-0 z-20 bg-white border-b border-[color:var(--color-border)]"
      style={{ boxShadow: '0 1px 12px rgba(245,194,0,0.08)' }}
    >
      <div className="max-w-7xl mx-auto flex items-center gap-4 px-6 py-3">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))',
            }}
          >
            <Sparkles size={18} color="#1A1A1A" />
          </div>
          <span className="font-extrabold tracking-tight text-lg">UNIQUE</span>
        </div>

        <span className="text-sm text-[color:var(--color-text-medium)] hidden md:inline">
          {now}
        </span>

        <nav className="ml-auto flex items-center gap-2">
          <AIStatusPill status={aiStatus} model={aiModel} />
          <Counter label="Q1" value={counts.Q1} tone="var(--color-urgent)" />
          <Counter label="Q2" value={counts.Q2} tone="var(--color-important)" />

          <button
            type="button"
            className="unique-btn-ghost !p-2 relative"
            aria-label="Notifications"
          >
            <Bell size={18} />
            {counts.Q1 > 0 && (
              <span
                className="absolute top-1 right-1 w-2 h-2 rounded-full"
                style={{ background: 'var(--color-urgent)' }}
                aria-hidden
              />
            )}
          </button>

          <div className="flex items-center gap-2 pl-2 border-l border-[color:var(--color-border)]">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: 'var(--color-primary-light)',
                color: 'var(--color-primary-dark)',
              }}
              aria-hidden
            >
              {initials}
            </div>
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-xs font-semibold">{fullName}</span>
              <span className="text-[11px] text-[color:var(--color-text-medium)]">
                {profile?.role ?? ''}
              </span>
            </div>
            <button
              type="button"
              onClick={logout}
              className="unique-btn-ghost !p-2"
              aria-label="Se déconnecter"
              title="Se déconnecter"
            >
              <LogOut size={16} />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}

function AIStatusPill({
  status,
  model,
}: {
  status: 'checking' | 'online' | 'offline';
  model: string;
}): JSX.Element {
  const online = status === 'online';
  const label =
    status === 'checking'
      ? 'IA…'
      : online
        ? model.split('/').pop() ?? 'IA'
        : 'IA hors ligne';
  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
      style={{
        background: online ? 'var(--color-primary-light)' : 'rgba(189,189,189,0.18)',
        border: `1px solid ${online ? 'var(--color-primary-dark)' : 'var(--color-defer)'}`,
        color: online ? 'var(--color-primary-dark)' : 'var(--color-text-medium)',
      }}
      title={online ? `Backend IA en ligne — ${model}` : 'Backend IA indisponible'}
    >
      {online ? <Zap size={12} /> : <ZapOff size={12} />}
      {label}
    </span>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{
        background: 'var(--color-primary-glow)',
        border: '1px solid var(--color-border)',
      }}
    >
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: tone }}
        aria-hidden
      />
      {label}
      <span className="text-[color:var(--color-text-medium)]">·</span>
      <span>{value}</span>
    </span>
  );
}
